import {
  Button,
  Checkbox,
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Box,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import auth from '../../../../apis/auth';
import Input from '../../../../components/Form/Input';
import Pagination from '../../../../components/Pagination';
import Restricted from '../../../../components/Restricted';
import UserModal from '../../../../components/UserModal';
import useAuth from '../../../../hooks/useAuth';
import useDebounce from '../../../../hooks/useDebounce';
import Item from './Item';

const List: React.FC = () => {
  const toast = useToast();
  const { debounce } = useDebounce();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [userToEdit, setUserToEdit] = useState<any | null>(null);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [email, setEmail] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cpf, setCpf] = useState('');
  const [withRole, setWithRole] = useState(false);

  const per_page = 7;
  const { t } = useTranslation();
  const { user: userTeste } = useAuth();
  const getUsers = async () => {
    const userHasPermission = userTeste?.permissions?.some(
      (permission: string) => permission === 'list_user',
    );

    if (userHasPermission) {
      try {
        setLoading(true);
        const body: any = {
          page,
          per_page,
          include_acl: true,
        };

        if (cpf) {
          body.cpf = cpf;
        }

        if (email) {
          body.email = email;
        }

        if (withRole) {
          body.only_users_with_roles = true;
        }

        const { data } = await auth.get<any>('users', {
          params: body,
        });
        setUsers(data.data);
        setTotal(data.data.length);

        // console.log('usuarios > ', data.data);
      } catch (err: any) {
        toast({
          title: 'Opss!!',
          description:
            // eslint-disable-next-line no-nested-ternary
            err?.response?.status === 403
              ? t('userWithoutPermission')
              : err?.response?.data?.message
              ? err?.response?.data?.message
              : t('generalError'),
          status: 'error',
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    } else {
      setUsers([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withRole, page, email, cpf]);

  return (
    <Flex
      flexDir="column"
      w="100%"
      p={8}
      overflow="auto"
      backgroundColor="#F9F9F9"
      css={{
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#469BFF',
          borderRadius: '24px',
        },
      }}
    >
      <Heading mb="48px" color="#469BFF" fontFamily="Poppins">
        {t('users')}
      </Heading>
      <Flex margin="0 0 40px 0">
        <Input
          name="email"
          bgColor="gray.200"
          placeholder={t('emailFilter')}
          width="199px"
          borderRadius="8px"
          backgroundColor="#FFFFFF"
          _placeholder={{ color: '#000000' }}
          onChange={e => {
            setLoading(true);
            debounce(() => {
              setEmail(e.target.value);
              setPage(1);
            }, 250);
          }}
        />
        <Input
          name="cpf"
          bgColor="gray.200"
          placeholder={t('cpfFilter')}
          width="199px"
          marginLeft="4"
          borderRadius="8px"
          backgroundColor="#FFFFFF"
          _placeholder={{ color: '#000000' }}
          onChange={e => {
            setLoading(true);
            debounce(() => {
              setCpf(e.target.value);
              setPage(1);
            }, 250);
          }}
        />
        <Restricted allowedPermissions={['create_user']}>
          <Button
            ml="4"
            aria-label="create_user"
            line-height="21px"
            textAlign="center"
            colorScheme="brand"
            fontFamily="Poppins"
            fontWeight="300"
            onClick={() => onOpen()}
          >
            {t('newUser')}
          </Button>
        </Restricted>
      </Flex>
      <Flex position="relative" flexDir="column" bg="#F9F9F9" p={8}>
        <Flex align="center" ml={4} mb={6}>
          <Checkbox
            mr={2}
            checked={withRole}
            onChange={() => {
              setPage(1);
              setWithRole(state => !state);
            }}
          >
            {t('onlyUsersWithPermissions')}
          </Checkbox>
        </Flex>
        <Box>
          <TableContainer>
            <Table variant="simple">
              <Thead
                border="1px solid #068FDD"
                borderRadius="14px"
                bg="#F9FDFF"
              >
                <Tr>
                  <Th textColor="#469BFF" fontFamily="Poppins">
                    {t('email')}
                  </Th>
                  <Th textColor="#469BFF" fontFamily="Poppins">
                    {t('cpfField')}
                  </Th>
                  <Th textColor="#469BFF" fontFamily="Poppins">
                    {t('officeLabel')}
                  </Th>
                  <Th textColor="#469BFF" fontFamily="Poppins">
                    {t('actions')}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map(user => (
                  <Item
                    user={user}
                    key={user?.id}
                    onDelete={id => {
                      setUsers(state =>
                        state.filter(oldUser => oldUser?.id !== id),
                      );
                    }}
                    onEditClick={(data: any) => {
                      setUserToEdit(data);
                      onOpen();
                    }}
                  />
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        <Pagination
          ml="auto"
          mt={6}
          disabled={loading}
          total={total}
          current={page}
          perPage={per_page}
          onPageChange={p => {
            setPage(p);
          }}
        />
        {loading && (
          <Flex
            position="absolute"
            align="center"
            justify="center"
            top="50%"
            left="50%"
          >
            <Spinner color="brand.500" size="xl" />
          </Flex>
        )}
        <UserModal
          isOpen={isOpen}
          userToEdit={userToEdit}
          onClose={() => {
            onClose();
            setUserToEdit(null);
          }}
          onCreated={user => {
            setUsers(state => [...state, user]);
          }}
          onEdited={user => {
            setUsers(state => {
              return state.map(stateUser => {
                if (stateUser?.id !== user?.id) return stateUser;
                return user;
              });
            });
          }}
        />
      </Flex>
    </Flex>
  );
};

export default List;
